import styles from './hastaPublicaListaProcessos.module.scss';
import { BrButton, BrLoading } from '@govbr-ds/react-components';

import { useContext, useEffect, useState, useCallback } from 'react';
import { DiefCfemContext } from '../../../../../contexts/DiefCfemContext';
import { IArrematacao, IArrematacaoListagem, IArrematacaoResumo } from '../../../../../interfaces/IArrematacao';
import { useBackEndRequests } from '../../../lib/backEndRequests';
import { ArrematacaoTable } from './components/aquisicaoTable/arrematacaoTable';
import { useNotificacao } from '../../../../../hooks/useNotificacao';

interface IHastaPublicaListaProcessosProps {
  setNovaArrematacao: React.Dispatch<React.SetStateAction<boolean>>;
  setEdicaoArrematacao: React.Dispatch<React.SetStateAction<IArrematacao | undefined>>;
}

function HastaPublicaListaProcessos({ setNovaArrematacao, setEdicaoArrematacao }: IHastaPublicaListaProcessosProps) {
  const headers = [
    { field: 'substancia', label: 'Substância' },
    { field: 'municipio', label: 'Município/UF' },
    { field: 'quantidade', label: 'Quantidade' },
    { field: 'unidadeMedida', label: 'Unidade' },
    { field: 'precoUnitario', label: 'Preço Unitário' },
    { field: 'valorArrematacao', label: 'Valor de Arrematação' },
    { field: 'acao', label: 'Operações' },
  ];

  const [salvarRascunhoText, setSalvarRascunhoText] = useState(
    <>
      <i className={`fas fa-save fa-md  ${styles.columnIcon}`} />
      <span> Salvar Rascunho</span>
    </>
  );

  const context = useContext(DiefCfemContext);
  const declaracaoHastaPublica = context?.declaracaoHastaPublica;
  const [arrematacaoListagemData, setArrematacaoListagemData] = useState<IArrematacaoListagem[]>([]);

  const mostrarNotificacao = useNotificacao();

  const { postSalvarRascunhoDeclaracao } = useBackEndRequests();

  const mapArrematacoesToListagem = useCallback(() => {
    const arrematacoes: IArrematacao[] = declaracaoHastaPublica?.arrematacoes || [];
    const resumos = mapArrematacoesToResumo(arrematacoes);
    const listagem = mapResumoToListagem(resumos);
    setArrematacaoListagemData(listagem);
  }, [declaracaoHastaPublica?.arrematacoes]);

  useEffect(() => {
    if (declaracaoHastaPublica?.arrematacoes) {
      mapArrematacoesToListagem();
    }
  }, [mapArrematacoesToListagem, declaracaoHastaPublica?.arrematacoes]);

  function mapArrematacoesToResumo(arrematacoes: IArrematacao[]): IArrematacaoResumo[] {
    const arrematacoesFuncao = arrematacoes.map((arrematacao) => ({
      arrematacao: arrematacao,
      quantidade: arrematacao?.componenteBaseDeCalculo?.quantidadeTotal,
      precoUnitario: (
        parseFloat(arrematacao?.componenteBaseDeCalculo?.valorTotal) /
        parseFloat(arrematacao.componenteBaseDeCalculo?.quantidadeTotal)
      ).toString(),
      valorArrematacao: arrematacao?.componenteBaseDeCalculo?.valorTotal,
    }));
    return arrematacoesFuncao;
  }

  function mapResumoToListagem(resumos: IArrematacaoResumo[]): IArrematacaoListagem[] {
    const agrupados: { [key: string]: IArrematacaoResumo[] } = {};

    resumos.forEach((resumo) => {
      if (resumo.arrematacao.numeroProcesso) {
        !agrupados[resumo.arrematacao.numeroProcesso] && (agrupados[resumo.arrematacao.numeroProcesso] = []);
        agrupados[resumo.arrematacao.numeroProcesso].push(resumo);
      } else {
        if (!agrupados[0]) {
          agrupados[0] = [];
        }
        agrupados[0].push(resumo);
      }
    });

    return Object.keys(agrupados).map((numeroProcesso) => ({
      numeroProcesso,
      titulo: 'Arrematação em Hasta Pública',
      arrematacoes: agrupados[numeroProcesso],
    }));
  }

  const salvarRascunho = async () => {
    setSalvarRascunhoText(
      <>
        <BrLoading />
      </>
    );

    if (declaracaoHastaPublica)
      await postSalvarRascunhoDeclaracao(declaracaoHastaPublica)
        .then(() => {
          setSalvarRascunhoText(
            <div className={styles.green}>
              <i className={`fas fa-check-circle fa-md  ${styles.columnIcon}`} />
              <span> Rascunho Salvo</span>
            </div>
          );
          setTimeout(() => {
            setSalvarRascunhoText(
              <>
                <i className={`fas fa-save fa-md  ${styles.columnIcon}`} />
                <span> Salvar Rascunho</span>
              </>
            );
          }, 5000);
        })
        .catch((err) =>
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao salvar rascunho',
            ...(err && { complementoSentryId: err }),
          })
        );
    else
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Erro ao salvar rascunho',
      });
  };

  return (
    <div className={styles.containerPrincipal}>
      <div className={styles.cabecalho}>
        <div className={styles.titleContainer}>
          <h4 className={styles.title}>Arrematações</h4>
        </div>
        <div className={styles.botoes}>
          <div className={styles.botao}>
            <BrButton
              secondary
              onClick={() => salvarRascunho()}
              disabled={!declaracaoHastaPublica?.arrematacoes || declaracaoHastaPublica?.arrematacoes?.length === 0}
            >
              {salvarRascunhoText}
            </BrButton>
          </div>
          <div className={styles.botao}>
            <BrButton primary onClick={() => setNovaArrematacao(true)}>
              <i className={`fas fa-plus fa-md  ${styles.columnIcon}`} />
              <span> Nova Arrematação</span>
            </BrButton>
          </div>
        </div>
      </div>
      <ArrematacaoTable
        data={arrematacaoListagemData || []}
        headers={headers}
        isLoading={false}
        mapArrematacoesToListagem={mapArrematacoesToListagem}
        setEdicaoArrematacao={setEdicaoArrematacao}
        setNovaArrematacao={setNovaArrematacao}
      />
    </div>
  );
}

export default HastaPublicaListaProcessos;
